if (!customElements.get('l20-split-switch')) {
  class L20SplitSwitch extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-l20-split-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-l20-split-panel]'));
      this.index = Math.max(
        0,
        this.tabs.findIndex((tab) => tab.classList.contains('is-active'))
      );
      this.enableScroll = this.dataset.scrollSwitch === 'true';
      this.autoplayVideo = this.dataset.autoplayVideo === 'true';
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileQuery = window.matchMedia('(max-width: 768.98px)');
      this.delta = 0;
      this.locked = false;

      if (this.tabs.length === 0) return;

      this.handleTabClick = this.onTabClick.bind(this);
      this.handlePlayClick = this.onPlayClick.bind(this);
      this.handleWheel = this.onWheel.bind(this);
      this.handleKeydown = this.onKeydown.bind(this);

      this.media = this.querySelector('.l20-split-switch__media');
      this.prevBtn = this.querySelector('[data-l20-split-prev]');
      this.nextBtn = this.querySelector('[data-l20-split-next]');
      this.progressDots = Array.from(this.querySelectorAll('[data-l20-split-progress]'));
      this.handlePointerDown = this.onPointerDown.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerUp = this.onPointerUp.bind(this);
      this.handlePrev = () => this.setActive(this.index - 1);
      this.handleNext = () => this.setActive(this.index + 1);
      this.handleProgress = (event) => this.setActive(Number(event.currentTarget.dataset.index));

      this.tabs.forEach((tab) => tab.addEventListener('click', this.handleTabClick));
      this.querySelectorAll('[data-l20-split-play]').forEach((button) => {
        button.addEventListener('click', this.handlePlayClick);
      });
      this.addEventListener('keydown', this.handleKeydown);
      this.media?.addEventListener('pointerdown', this.handlePointerDown);
      this.prevBtn?.addEventListener('click', this.handlePrev);
      this.nextBtn?.addEventListener('click', this.handleNext);
      this.progressDots.forEach((dot) => dot.addEventListener('click', this.handleProgress));

      if (this.enableScroll && !this.reduceMotion) {
        window.addEventListener('wheel', this.handleWheel, { passive: false });
      }

      this.setActive(this.index, true);
    }

    disconnectedCallback() {
      this.tabs?.forEach((tab) => tab.removeEventListener('click', this.handleTabClick));
      this.querySelectorAll('[data-l20-split-play]').forEach((button) => {
        button.removeEventListener('click', this.handlePlayClick);
      });
      this.removeEventListener('keydown', this.handleKeydown);
      this.media?.removeEventListener('pointerdown', this.handlePointerDown);
      this.media?.removeEventListener('pointermove', this.handlePointerMove);
      this.media?.removeEventListener('pointerup', this.handlePointerUp);
      this.media?.removeEventListener('pointercancel', this.handlePointerUp);
      this.prevBtn?.removeEventListener('click', this.handlePrev);
      this.nextBtn?.removeEventListener('click', this.handleNext);
      this.progressDots?.forEach((dot) => dot.removeEventListener('click', this.handleProgress));
      window.removeEventListener('wheel', this.handleWheel);
    }

    isInFocus() {
      const rect = this.getBoundingClientRect();
      const mid = window.innerHeight * 0.5;
      return rect.top < mid && rect.bottom > mid;
    }

    onTabClick(event) {
      const index = Number(event.currentTarget.dataset.index);
      this.setActive(index);
    }

    onPlayClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.playPanel(event.currentTarget.closest('[data-l20-split-panel]'));
    }

    onPointerDown(event) {
      if (!this.mobileQuery.matches || event.pointerType === 'mouse' && event.button !== 0) return;
      this.swipeX = event.clientX;
      this.swipeDX = 0;
      this.swiping = true;
      this.media?.addEventListener('pointermove', this.handlePointerMove);
      this.media?.addEventListener('pointerup', this.handlePointerUp);
      this.media?.addEventListener('pointercancel', this.handlePointerUp);
    }

    onPointerMove(event) {
      if (!this.swiping) return;
      this.swipeDX = event.clientX - this.swipeX;
    }

    onPointerUp() {
      this.media?.removeEventListener('pointermove', this.handlePointerMove);
      this.media?.removeEventListener('pointerup', this.handlePointerUp);
      this.media?.removeEventListener('pointercancel', this.handlePointerUp);
      if (!this.swiping) return;
      this.swiping = false;
      if (Math.abs(this.swipeDX) < 46) return;
      if (this.swipeDX < 0) this.setActive(this.index + 1);
      else this.setActive(this.index - 1);
    }

    onKeydown(event) {
      if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(event.key)) return;
      if (!this.contains(document.activeElement)) return;

      const goingNext = event.key === 'ArrowDown' || event.key === 'ArrowRight';
      const nextIndex = goingNext ? this.index + 1 : this.index - 1;
      if (nextIndex < 0 || nextIndex >= this.tabs.length) return;

      event.preventDefault();
      this.setActive(nextIndex);
      this.tabs[nextIndex].focus({ preventScroll: true });
    }

    onWheel(event) {
      if (this.mobileQuery.matches || this.tabs.length < 2 || !this.isInFocus()) return;

      const goingDown = event.deltaY > 0;
      if ((goingDown && this.index >= this.tabs.length - 1) || (!goingDown && this.index <= 0)) {
        return;
      }

      event.preventDefault();
      if (this.locked) return;

      this.delta += event.deltaY;
      if (Math.abs(this.delta) < 56) return;

      const nextIndex = this.delta > 0 ? this.index + 1 : this.index - 1;
      this.delta = 0;
      this.setActive(nextIndex);
    }

    setActive(index, initial = false) {
      if (index < 0 || index >= this.tabs.length) return;
      if (!initial && index === this.index) return;

      this.index = index;
      this.locked = true;
      window.setTimeout(() => {
        this.locked = false;
        this.delta = 0;
      }, 420);

      this.tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.classList.toggle('is-active', selected);
        tab.classList.toggle('is-expanded', selected);
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel, panelIndex) => {
        const selected = panelIndex === index;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;

        if (selected) {
          if (this.autoplayVideo) this.playPanel(panel);
        } else {
          this.pausePanel(panel);
        }
      });

      this.progressDots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
      });
      if (this.prevBtn) this.prevBtn.disabled = index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = index >= this.tabs.length - 1;
    }

    activeVideo(panel) {
      if (!panel) return null;
      return Array.from(panel.querySelectorAll('video')).find((video) => getComputedStyle(video).display !== 'none') || null;
    }

    async playPanel(panel) {
      const video = this.activeVideo(panel);
      if (!video) return;

      try {
        video.muted = true;
        await video.play();
        panel.classList.add('is-playing');
      } catch (error) {
        panel.classList.remove('is-playing');
      }
    }

    pausePanel(panel) {
      panel.classList.remove('is-playing');
      panel.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    }
  }

  customElements.define('l20-split-switch', L20SplitSwitch);
}
